import React from 'react'

import {
  CampaignInvite,
  FunnelStats,
  isApplied,
  isEnrolled,
} from '../../services/campaignInvite'

// ----------------------------------------------------------------------------
// Mini-funnel d'invitation. Deux usages :
//  - mode "count"    : bandeau global de stats (Invite -> Vu -> Inscrit -> Postule -> Gagne)
//  - mode "progress" : etat d'un createur dans la conversation (etapes atteintes)
// ----------------------------------------------------------------------------

interface Step {
  key: string
  label: string
  value?: number
  reached?: boolean
}

const Pill: React.FC<{ active: boolean; label: string; value?: number }> = ({
  active,
  label,
  value,
}) => (
  <div
    className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${
      active
        ? 'bg-[#7885ed] text-white'
        : 'bg-[#eef0fb] text-[#9aa0b8]'
    }`}
  >
    {typeof value === 'number' && <span className="tabular-nums">{value}</span>}
    <span>{label}</span>
  </div>
)

const FunnelBar: React.FC<{ steps: Step[]; mode: 'count' | 'progress' }> = ({
  steps,
  mode,
}) => (
  <div className="flex items-center gap-1.5 flex-wrap">
    {steps.map((s, i) => (
      <React.Fragment key={s.key}>
        <Pill
          active={mode === 'progress' ? !!s.reached : (s.value ?? 0) > 0}
          label={s.label}
          value={mode === 'count' ? s.value ?? 0 : undefined}
        />
        {i < steps.length - 1 && (
          <i className="fa-solid fa-angle-right text-[#c5cae8] text-[10px]" />
        )}
      </React.Fragment>
    ))}
  </div>
)

// Bandeau global de stats du funnel d'invitation (compteurs).
export const InviteStatsBanner: React.FC<{ stats: FunnelStats }> = ({ stats }) => {
  if (stats.invited === 0) return null
  const steps: Step[] = [
    { key: 'invited', label: 'Invités', value: stats.invited },
    { key: 'opened', label: 'Vus', value: stats.opened },
    { key: 'enrolled', label: 'Inscrits', value: stats.enrolled },
    { key: 'applied', label: 'Ont postulé', value: stats.applied },
    { key: 'won', label: 'Sélectionnés', value: stats.won },
  ]
  const conv =
    stats.invited > 0 ? Math.round((stats.applied / stats.invited) * 100) : 0
  return (
    <div className="rounded-2xl border border-[#e6e8f2] bg-white p-3 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-[#3A4161]">
          <i className="fa-solid fa-bullhorn mr-1.5 text-[#7885ed]" />
          Funnel d&apos;invitations
        </span>
        <span className="text-xs font-semibold text-[#7885ed]">
          {conv}% de conversion
        </span>
      </div>
      <FunnelBar steps={steps} mode="count" />
    </div>
  )
}

// Carte contexte campagne pour un createur invite (dans la conversation).
export const InviteContextCard: React.FC<{ invites: CampaignInvite[] }> = ({
  invites,
}) => {
  if (!invites || invites.length === 0) return null

  // Etat agrege du createur (s'il a ete invite sur plusieurs campagnes, on prend
  // le meilleur statut atteint pour refleter sa progression globale).
  const opened = invites.some(
    (i) => i.inviteStatus === 'opened' || !!i.applicationStatus
  )
  const enrolled = invites.some((i) => isEnrolled(i.applicationStatus))
  const applied = invites.some((i) => isApplied(i.applicationStatus))
  const won = invites.some((i) => i.applicationStatus === 'won')

  const steps: Step[] = [
    { key: 'invited', label: 'Invité', reached: true },
    { key: 'opened', label: 'Vu', reached: opened },
    { key: 'enrolled', label: 'Inscrit', reached: enrolled },
    { key: 'applied', label: 'A postulé', reached: applied },
    { key: 'won', label: 'Sélectionné', reached: won },
  ]

  const campaignNames = Array.from(
    new Set(invites.map((i) => i.brief?.name).filter(Boolean))
  ) as string[]

  return (
    <div className="rounded-xl border border-[#e6e8f2] bg-[#f8f9ff] p-3 flex flex-col gap-2 my-2">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-[#3A4161]">
        <i className="fa-solid fa-paper-plane text-[#7885ed]" />
        {campaignNames.length > 0
          ? `Invité à : ${campaignNames.join(', ')}`
          : 'Créateur invité à une campagne'}
      </div>
      <FunnelBar steps={steps} mode="progress" />
    </div>
  )
}

export default InviteStatsBanner
